'use client'

import {useMemo, useState, type ReactNode} from 'react'
import Image from 'next/image'
import {sanityImageLoader} from '@/sanity/imageLoader'
import {ProductBrandBadge, getProductBrandLogo} from './ProductBrandBadge'
import type {ProductDocument} from '@/types/sanity'
import {
  productFromPriceForMarket,
  productHeroImageUrl,
  productPricesForMarket,
  productVariantPriceForMarket,
  productVariantSwatchColor,
} from '@/components/products/productDisplay'
import {formatPrice} from '@/lib/formatting'
import {t} from '@/lib/i18n'
import {sanitizeHtml} from '@/lib/sanitizeHtml'
import {ProductEnquirySheet} from '@/components/products/ProductEnquirySheet'
import {EnergyClassArrow} from '@/components/shared/EnergyClassArrow'
import {ToggleGroup, ToggleGroupItem} from '@/components/ui/toggle-group'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  type CarouselApi,
} from '@/components/ui/carousel'

type ProductDetailHeroProps = {
  product: ProductDocument
  locale?: string
  widgetLocale?: string
  widgetMarket?: string
  widgetMarkets?: string[]
  isDefaultSite?: boolean
  hideProductPrices?: boolean
  priceAccessory?: ReactNode
  series: string
  typeLabel?: string
  title: string
  description?: string
  discontinued?: boolean
  discontinuedTitle?: string
  discontinuedDescription?: string
}

type GalleryImage = {
  id: string
  url?: string
  alt: string
  variantKey?: string
}

const HERO_BACKDROP_IMAGE =
  'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80'
const HERO_BACKDROP_OVERLAY = 'linear-gradient(180deg, rgba(16,23,34,0.85), rgba(16,23,34,0.7))'

const optimizeGalleryImageUrl = (rawUrl?: string) => {
  if (!rawUrl) return rawUrl

  try {
    const parsed = new URL(rawUrl)
    if (parsed.hostname === 'cdn.sanity.io') {
      parsed.searchParams.set('auto', 'format')
      parsed.searchParams.set('fit', 'max')
      parsed.searchParams.set('w', '1600')
      parsed.searchParams.set('q', '75')
    }
    return parsed.toString()
  } catch {
    return rawUrl
  }
}

const DESCRIPTION_TRUNCATE_LENGTH = 150

function DescriptionBlock({description, locale}: {description: string; locale?: string}) {
  const [expanded, setExpanded] = useState(false)
  const descriptionHtml = sanitizeHtml(description)
  const needsTruncation = description.length > DESCRIPTION_TRUNCATE_LENGTH

  return (
    <div className="mt-site-sm">
      <div
        className={`u-body-md text-foreground u-wysiwyg ${!expanded && needsTruncation ? 'line-clamp-3' : ''}`}
        dangerouslySetInnerHTML={{__html: descriptionHtml}}
      />
      {needsTruncation ? (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="mt-1 u-body-sm font-semibold text-foreground underline underline-offset-2"
        >
          {expanded
            ? t(locale, 'product.readLess', 'Read less')
            : t(locale, 'product.readMore', 'Read more')}
        </button>
      ) : null}
    </div>
  )
}

export function ProductDetailHero({
  product,
  locale,
  widgetLocale,
  widgetMarket,
  widgetMarkets,
  isDefaultSite,
  hideProductPrices = false,
  priceAccessory,
  series,
  typeLabel,
  title,
  description,
  discontinued = false,
  discontinuedTitle,
  discontinuedDescription,
}: ProductDetailHeroProps) {
  const images = useMemo<GalleryImage[]>(() => {
    const items: GalleryImage[] = []
    const heroUrl = productHeroImageUrl(product)

    if (heroUrl) {
      items.push({
        id: `${product._id}-hero`,
        url: optimizeGalleryImageUrl(heroUrl),
        alt: title,
      })
    }

    for (const variant of product.variants || []) {
      const variantKey = variant._key || variant.productNumber
      for (const image of variant.images || []) {
        items.push({
          id: image._key || `${variantKey || 'variant'}-${image.mediaCode || image.url || 'image'}`,
          url: optimizeGalleryImageUrl(image.url),
          alt: `${title} ${variant.color || variant.productNumber || ''}`.trim(),
          variantKey,
        })
      }
    }

    return items.filter(
      (item, index, array) =>
        item.url &&
        array.findIndex(
          (candidate) => candidate.url === item.url && candidate.variantKey === item.variantKey,
        ) === index,
    )
  }, [product, title])

  const [api, setApi] = useState<CarouselApi>()
  const [selectedVariantKey, setSelectedVariantKey] = useState<string | undefined>()
  const activeVariantKey = selectedVariantKey
  const brandLabel = product.brand?.title || product.brandKey || series
  const brandLogo = getProductBrandLogo(product.brandKey, product.brand?.title)

  // Gallery images used to be preloaded here with `new window.Image()` so
  // carousel transitions had nothing to wait for. That fetched every variant
  // image at the fixed w=1600 baked into the URL — on a phone, six 1600px
  // downloads to fill a ~390px box, and a second set of fetches besides the
  // ones the rendered <img> made.
  //
  // The slides are all in the DOM already, so `loading="eager"` below gets the
  // same "everything is ready before you swipe" behaviour through the element
  // the browser was going to fetch anyway, at the width the srcset picks.
  // Removing the manual preload is what makes that a saving rather than a
  // second copy (JO2-206).

  const variants = (product.variants || []).map((variant) => ({
    key: variant._key || variant.productNumber || variant.color || 'variant',
    label: variant.color || variant.productNumber || 'Variant',
    colorCode: variant.color,
    colorHex: variant.colorHex,
    imageIndex: images.findIndex(
      (image) => image.variantKey === (variant._key || variant.productNumber),
    ),
    price: productVariantPriceForMarket(variant, widgetMarket),
    productNumber: variant.productNumber,
  }))

  const selectedVariant = variants.find((variant) => variant.key === activeVariantKey)
  const pricedVariants = hideProductPrices ? [] : productPricesForMarket(product, widgetMarket)
  const fromPrice = hideProductPrices ? null : productFromPriceForMarket(product, widgetMarket)
  const selectedVariantPrice = !hideProductPrices && selectedVariant ? selectedVariant.price : null
  const displayedPrice = selectedVariantPrice || fromPrice

  return (
    <section className="relative overflow-hidden text-white">
      <Image
        src={HERO_BACKDROP_IMAGE}
        alt=""
        fill
        priority
        sizes="100vw"
        aria-hidden
        className="-z-20 object-cover"
      />
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{background: HERO_BACKDROP_OVERLAY}}
      />
      <div className="container relative grid items-stretch gap-card-gap pt-site-2xl pb-[calc(var(--spacing-card-gap)*3)] lg:grid-cols-2">
        <div className="relative self-start">
          <Carousel
            opts={{loop: true}}
            setApi={setApi}
            aria-label={t(locale, 'product.imageGalleryAriaLabel', 'Product image gallery')}
            className="overflow-hidden rounded-card border border-white/20 bg-white/10 shadow-2xl [&_[data-slot=carousel-content]]:!pb-0 [&_[data-slot=carousel-content]]:!px-0 [&_[data-slot=carousel-content]]:!mx-0 [&_[data-slot=carousel-content]>div]:!ml-0"
          >
            <CarouselContent className="!m-0 !p-0">
              {images.map((image) => (
                <CarouselItem key={image.id} className="!p-0">
                  <div className="relative aspect-square bg-white/10">
                    {image.url ? (
                      <Image
                        src={image.url}
                        alt={image.alt}
                        fill
                        sizes="(min-width: 1024px) 50vw, 100vw"
                        loader={sanityImageLoader}
                        loading="eager"
                        // object-contain so the gallery accepts mixed source
                        // aspect ratios (square, portrait 1000x1700, landscape
                        // 1600x918) without cropping. The aspect-square +
                        // bg-white/10 wrapper above shows as the breathing
                        // room around non-square images.
                        className="object-contain"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-[linear-gradient(135deg,_rgba(255,255,255,0.22),_rgba(255,255,255,0.06))]" />
                    )}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            {images.length > 1 && (
              <>
                <CarouselPrevious className="!left-4 top-1/2 cursor-pointer bg-white/90 text-foreground shadow-lg hover:bg-white md:!left-4" />
                <CarouselNext className="!right-4 top-1/2 cursor-pointer bg-white/90 text-foreground shadow-lg hover:bg-white md:!right-4" />
              </>
            )}
          </Carousel>
          <div className="pointer-events-none absolute left-3 top-3 z-20 p-2">
            <ProductBrandBadge logo={brandLogo} label={brandLabel} size={64} />
          </div>
        </div>

        <div
          className="u-card-glass text-foreground"
          style={{
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
          }}
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            {series}
            {typeLabel ? ` | ${typeLabel}` : ''}
          </p>
          <h1 className="mt-1 u-head-md text-foreground">{title}</h1>
          {/* Article number intentionally hidden (client request, JO2 2026-07): the imported
              productNumber is the fictive BASE/mother PIM number, not the actual item, which
              confused dealers. Re-enable here (or add a CMS visibility toggle) if that changes. */}
          {discontinued ? (
            <div className="mt-4 space-y-2">
              <p className="text-lg font-semibold text-foreground">
                {discontinuedTitle || `${title} has unfortunately been discontinued`}
              </p>
              <p className="text-sm text-muted-foreground">
                {discontinuedDescription ||
                  "On this page you'll find user manuals, information about spare parts and accessories as well as alternative fireplaces."}
              </p>
            </div>
          ) : null}

          {!discontinued && pricedVariants.length && fromPrice ? (
            <div className="mt-site-lg">
              <p className="u-head-sm text-foreground">
                {selectedVariantPrice ? (
                  <>
                    <span className="font-bold">
                      {formatPrice(selectedVariantPrice.amount)}{' '}
                      {selectedVariantPrice.currency || 'NOK'}
                    </span>
                    {selectedVariantPrice.originalAmount ? (
                      <span className="ml-2 text-base font-normal text-muted-foreground line-through">
                        {formatPrice(selectedVariantPrice.originalAmount)}{' '}
                        {selectedVariantPrice.currency || 'NOK'}
                      </span>
                    ) : null}
                  </>
                ) : (
                  <>
                    <span className="font-normal">
                      {t(locale, 'product.priceFromPrefix', 'From')}{' '}
                    </span>
                    <span className="font-bold">
                      {formatPrice(fromPrice.amount)} {fromPrice.currency || 'NOK'}
                    </span>
                    {fromPrice.originalAmount ? (
                      <span className="ml-2 text-base text-muted-foreground line-through">
                        {formatPrice(fromPrice.originalAmount)} {fromPrice.currency || 'NOK'}
                      </span>
                    ) : null}
                  </>
                )}
              </p>
              {displayedPrice && priceAccessory ? (
                <div className="mt-0.5 u-body-xs text-muted-foreground">{priceAccessory}</div>
              ) : null}
              <p className="mt-1 u-body-xs text-muted-foreground">
                {t(locale, 'product.priceNote', 'Suggested retail price incl. VAT')}
              </p>
            </div>
          ) : null}

          {description ? <DescriptionBlock description={description} locale={locale} /> : null}

          <div className="mt-site-lg space-y-site-sm">
            {!discontinued ? (
              <div className="space-y-site-xs">
                <p className="u-label text-foreground">{t(locale, 'product.colors', 'Colors')}</p>
                {variants.length ? (
                  <div className="flex flex-wrap items-center gap-site-xs">
                    <ToggleGroup
                      value={selectedVariant?.key ? [selectedVariant.key] : []}
                      onValueChange={(pressed) => {
                        const key = pressed[pressed.length - 1]
                        if (!key) return
                        setSelectedVariantKey(key)
                        const v = variants.find((variant) => variant.key === key)
                        if (v && v.imageIndex >= 0) api?.scrollTo(v.imageIndex)
                      }}
                      className="flex-wrap gap-site-xs"
                    >
                      {variants.map((variant) => {
                        const swatchColor = productVariantSwatchColor(
                          variant.colorHex,
                          variant.colorCode,
                        )

                        return (
                          <ToggleGroupItem
                            key={variant.key}
                            value={variant.key}
                            className="!size-9 !rounded-full !border-2 !border-border !p-0 !bg-transparent cursor-pointer hover:!bg-transparent aria-pressed:!bg-transparent data-[pressed]:!border-primary data-[pressed]:shadow-[0_0_0_4px_rgba(240,93,43,0.18)]"
                            title={variant.label}
                            aria-label={variant.label}
                          >
                            <span
                              className="inline-flex size-6 rounded-full border border-black/10"
                              style={{background: swatchColor}}
                            />
                          </ToggleGroupItem>
                        )
                      })}
                    </ToggleGroup>
                    {product.ecoLabel ? (
                      <EnergyClassArrow
                        ecoLabel={product.ecoLabel}
                        className="ml-2 inline-flex origin-left scale-[0.88]"
                      />
                    ) : null}
                  </div>
                ) : (
                  <p className="u-body-md text-muted-foreground">
                    {t(
                      locale,
                      'product.colorsEmpty',
                      'Variant colors will appear here when available.',
                    )}
                  </p>
                )}
              </div>
            ) : null}

            {!discontinued ? (
              <div className="w-full">
                <ProductEnquirySheet
                  productId={product.productNumber}
                  productVariant={selectedVariant?.productNumber || variants[0]?.productNumber}
                  productTitle={title}
                  locale={widgetLocale || locale}
                  market={widgetMarket}
                  markets={widgetMarkets}
                  brandKey={product.brandKey || product.brand?.key}
                  brand={product.brand?.title}
                  isDefaultSite={isDefaultSite}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  )
}
